import {z} from 'zod';

import {isNotNil} from 'ramda';

import type {ApiError} from 'shared/web';

const CASH_LIMIT_VIOLATION_CODE = 'DMS-AC-CD-0013';

// Anchored on the JSON `code` field so an unrelated failure that merely echoes the code back
// (e.g. an invalid id repeated in a "not found" message) is not read as a limit violation.
const WRAPPED_VIOLATION_CODE = new RegExp(`"code"\\s*:\\s*"${CASH_LIMIT_VIOLATION_CODE}"`);

// Status is deliberately not pinned - the ESB proxy's outer status for the wrapped envelope is not
// contract-guaranteed, and this predicate only ever sees failures of the pay mutations.
const structuredErrorSchema = z.object({
  data: z.object({errors: z.array(z.object({code: z.string().nullish()}).nullish())}),
});

// The ESB checkout proxy wraps the accounting 400 into an IntegrationException string, so the code
// has to be matched inside `error.message` as well. Parsed separately from the structured shape so
// one malformed `errors` entry cannot disable this check.
const wrappedErrorSchema = z.object({
  data: z.object({error: z.object({message: z.string()})}),
});

export const isCashLimitViolationError = (error?: ApiError) => {
  const structured = structuredErrorSchema.safeParse(error);
  const wrapped = wrappedErrorSchema.safeParse(error);

  const codes = structured.success
    ? structured.data.data.errors.map((item) => item?.code).filter(isNotNil)
    : [];

  // Every coded error has to be the violation, otherwise a co-occurring failure would lose its own
  // message to the limit toast. The wrapped match needs the same guard - a payload can carry both
  // shapes, and then the structured error is the one with something actionable to say.
  const hasNoConflictingCode = codes.every((code) => code === CASH_LIMIT_VIOLATION_CODE);

  const hasViolationCode = codes.length > 0 && hasNoConflictingCode;

  const hasWrappedViolationCode =
    wrapped.success &&
    hasNoConflictingCode &&
    WRAPPED_VIOLATION_CODE.test(wrapped.data.data.error.message);

  return hasViolationCode || hasWrappedViolationCode;
};
